import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/ui/components/button';
import { Input } from '@/shared/ui/components/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/components/form';
import { login, LoginError, type LoginCredentials } from './api';
import { AUTH_ME_QUERY_KEY } from './AuthContext';

const schema = z.object({
  email: z.string().email(),
  password: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

export function LoginPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [params] = useSearchParams();
  const from = params.get('from') || '/';

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  const mutation = useMutation({
    mutationFn: (values: LoginCredentials) => login(values),
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: AUTH_ME_QUERY_KEY });
      navigate(from, { replace: true });
    },
  });

  const submitError =
    mutation.error instanceof LoginError
      ? mutation.error.status === 401
        ? t('auth.errors.invalidCredentials')
        : t('auth.errors.generic')
      : mutation.error
        ? t('auth.errors.generic')
        : null;

  return (
    <section className="mx-auto grid max-w-sm gap-6 p-6">
      <h1 className="text-2xl font-semibold">{t('auth.login.title')}</h1>
      <Form {...form}>
        <form
          className="grid gap-4"
          onSubmit={form.handleSubmit((values) => mutation.mutate(values))}
          noValidate
        >
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.login.email')}</FormLabel>
                <FormControl>
                  <Input type="email" autoComplete="username" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t('auth.login.password')}</FormLabel>
                <FormControl>
                  <Input type="password" autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {submitError && (
            <p role="alert" className="text-destructive text-sm">
              {submitError}
            </p>
          )}
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending ? t('auth.login.submitting') : t('auth.login.submit')}
          </Button>
        </form>
      </Form>
    </section>
  );
}
